/**
 * 增强的缓存管理器
 * 支持多种存储方式：内存、localStorage、IndexedDB、Service Worker
 */
import errorHandler from './errorHandler';

class EnhancedCacheManager {
  constructor() {
    this.memoryCache = new Map();
    this.defaultTTL = 60 * 1000; // 1分钟
    this.imageTTL = 24 * 60 * 60 * 1000; // 1天
    this.resourceTTL = 7 * 24 * 60 * 60 * 1000; // 7天
    
    this.initIndexedDB();
  }

  /**
   * 初始化IndexedDB
   */
  initIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('MihuanGameCache', 1);
      
      request.onerror = () => {
        console.error('IndexedDB初始化失败');
        reject(request.error);
      };
      
      request.onsuccess = () => {
        this.db = request.result;
        console.log('IndexedDB初始化成功');
        resolve();
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // 创建对象存储
        if (!db.objectStoreNames.contains('resources')) {
          const resourceStore = db.createObjectStore('resources', { keyPath: 'url' });
          resourceStore.createIndex('type', 'type', { unique: false });
          resourceStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        if (!db.objectStoreNames.contains('images')) {
          const imageStore = db.createObjectStore('images', { keyPath: 'url' });
          imageStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
        
        if (!db.objectStoreNames.contains('api')) {
          const apiStore = db.createObjectStore('api', { keyPath: 'key' });
          apiStore.createIndex('timestamp', 'timestamp', { unique: false });
        }
      };
    });
  }


  /**
   * 缓存资源到IndexedDB
   * @param {string} url 资源URL
   * @param {any} data 资源数据
   * @param {string} type 资源类型
   * @param {number} ttl 过期时间
   */
  async cacheResource(url, data, type = 'resource', ttl = this.resourceTTL) {
    try {
      const transaction = this.db.transaction(['resources'], 'readwrite');
      const store = transaction.objectStore('resources');
      
      const cacheData = {
        url: url,
        data: data,
        type: type,
        timestamp: Date.now(),
        expireTime: Date.now() + ttl
      };
      
      await store.put(cacheData);
      console.log(`资源已缓存到IndexedDB: ${url}`);
      
      // 同时缓存到内存
      this.memoryCache.set(url, cacheData);
      
    } catch (error) {
      console.error('缓存资源失败:', error);
      errorHandler.handleImageError(error, url);
    }
  }

  /**
   * 从IndexedDB获取缓存的资源
   * @param {string} url 资源URL
   * @returns {Promise<any>} 缓存的资源数据
   */
  async getCachedResource(url) {
    try {
      // 先检查内存缓存
      const memoryData = this.memoryCache.get(url);
      if (memoryData && Date.now() < memoryData.expireTime) {
        console.log(`从内存缓存获取: ${url}`);
        return memoryData.data;
      }
      
      // 从IndexedDB获取
      const transaction = this.db.transaction(['resources'], 'readonly');
      const store = transaction.objectStore('resources');
      const request = store.get(url);
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const result = request.result;
          if (result && Date.now() < result.expireTime) {
            console.log(`从IndexedDB缓存获取: ${url}`);
            // 更新内存缓存
            this.memoryCache.set(url, result);
            resolve(result.data);
          } else {
            // 缓存过期，删除
            if (result) {
              this.deleteCachedResource(url);
            }
            resolve(null);
          }
        };
        
        request.onerror = () => {
          reject(request.error);
        };
      });
      
    } catch (error) {
      console.error('获取缓存资源失败:', error);
      return null;
    }
  }

  /**
   * 删除缓存的资源
   * @param {string} url 资源URL
   */
  async deleteCachedResource(url) {
    try {
      // 从IndexedDB删除
      const transaction = this.db.transaction(['resources'], 'readwrite');
      const store = transaction.objectStore('resources');
      await store.delete(url);
      
      // 从内存删除
      this.memoryCache.delete(url);
      
      console.log(`已删除缓存资源: ${url}`);
    } catch (error) {
      console.error('删除缓存资源失败:', error);
    }
  }

  /**
   * 缓存图片
   * @param {string} url 图片URL
   * @param {string} base64Data 图片的base64数据
   */
  async cacheImage(url, base64Data) {
    try {
      const transaction = this.db.transaction(['images'], 'readwrite');
      const store = transaction.objectStore('images');
      
      const imageData = {
        url: url,
        data: base64Data,
        timestamp: Date.now(),
        expireTime: Date.now() + this.imageTTL
      };
      
      await store.put(imageData);
      console.log(`图片已缓存: ${url}`);
      
    } catch (error) {
      console.error('缓存图片失败:', error);
      errorHandler.handleImageError(error, url);
    }
  }

  /**
   * 获取缓存的图片
   * @param {string} url 图片URL
   * @returns {Promise<string|null>} 图片的base64数据
   */
  async getCachedImage(url) {
    try {
      const transaction = this.db.transaction(['images'], 'readonly');
      const store = transaction.objectStore('images');
      const request = store.get(url);
      
      return new Promise((resolve, reject) => {
        request.onsuccess = () => {
          const result = request.result;
          if (result && Date.now() < result.expireTime) {
            console.log(`从缓存获取图片: ${url}`);
            resolve(result.data);
          } else {
            if (result) {
              this.deleteCachedImage(url);
            }
            resolve(null);
          }
        };
        
        request.onerror = () => {
          reject(request.error);
        };
      });
      
    } catch (error) {
      console.error('获取缓存图片失败:', error);
      return null;
    }
  }

  /**
   * 删除缓存的图片
   * @param {string} url 图片URL
   */
  async deleteCachedImage(url) {
    try {
      const transaction = this.db.transaction(['images'], 'readwrite');
      const store = transaction.objectStore('images');
      await store.delete(url);
      console.log(`已删除缓存图片: ${url}`);
    } catch (error) {
      console.error('删除缓存图片失败:', error);
    }
  }

  /**
   * 加载图片（增强版）
   * @param {string} url 图片URL
   * @param {number} retryCount 重试次数
   * @returns {Promise<string>} 图片的base64数据或URL
   */
  async loadImage(url, retryCount = 3) {
    // 先尝试从缓存获取
    const cachedData = await this.getCachedImage(url);
    if (cachedData) {
      return cachedData;
    }
    
    // 缓存中没有，从网络获取
    for (let attempt = 1; attempt <= retryCount; attempt++) {
      try {
        console.log(`尝试加载图片 (第${attempt}次): ${url}`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);
        
        const response = await fetch(url, {
          signal: controller.signal,
          mode: 'cors',
          cache: 'no-cache',
          headers: {
            'Accept': 'image/*',
            'Cache-Control': 'no-cache'
          }
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const blob = await response.blob();
        
        if (blob.size === 0) {
          throw new Error('图片数据为空');
        }
        
        const base64 = await this.blobToBase64(blob);
        
        // 缓存图片
        await this.cacheImage(url, base64);
        
        console.log(`图片加载成功: ${url}`);
        return base64;
        
      } catch (error) {
        console.warn(`图片加载失败 (第${attempt}次): ${url}`, error.message);
        errorHandler.handleImageError(error, url);
        
        if (attempt === retryCount) {
          console.error('图片加载最终失败:', error);
          return this.getFallbackImage(url);
        }
        
        await this.delay(1000 * attempt);
      }
    }
  }

  /**
   * 预加载资源
   * @param {Array<string>} urls 资源URL数组
   */
  async preloadResources(urls) {
    console.log('开始预加载资源:', urls);
    
    const promises = urls.map(async (url) => {
      try {
        if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i)) {
          await this.loadImage(url);
        } else {
          const response = await fetch(url);
          if (response.ok) {
            const data = await response.text();
            await this.cacheResource(url, data, 'resource');
          }
        }
      } catch (error) {
        console.warn(`预加载资源失败: ${url}`, error);
      }
    });
    
    await Promise.allSettled(promises);
    console.log('资源预加载完成');
  }

  /**
   * 清理过期缓存
   */
  async cleanExpiredCache() {
    try {
      const now = Date.now();
      
      // 清理内存缓存
      for (const [key, item] of this.memoryCache.entries()) {
        if (now > item.expireTime) {
          this.memoryCache.delete(key);
        }
      }
      
      // 清理IndexedDB缓存
      const transaction = this.db.transaction(['resources', 'images', 'api'], 'readwrite');
      
      // 清理资源缓存
      const resourceStore = transaction.objectStore('resources');
      const resourceIndex = resourceStore.index('timestamp');
      const resourceRequest = resourceIndex.openCursor();
      
      resourceRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (now > cursor.value.expireTime) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
      
      // 清理图片缓存
      const imageStore = transaction.objectStore('images');
      const imageIndex = imageStore.index('timestamp');
      const imageRequest = imageIndex.openCursor();
      
      imageRequest.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (now > cursor.value.expireTime) {
            cursor.delete();
          }
          cursor.continue();
        }
      };
      
      console.log('过期缓存清理完成');
      
    } catch (error) {
      console.error('清理过期缓存失败:', error);
    }
  }

  /**
   * 获取缓存统计信息
   * @returns {Promise<Object>} 缓存统计信息
   */
  async getCacheStats() {
    try {
      const stats = {
        memory: {
          size: this.memoryCache.size,
          items: Array.from(this.memoryCache.keys())
        },
        indexedDB: {
          resources: 0,
          images: 0,
          api: 0
        }
      };
      
      // 统计IndexedDB中的缓存数量
      const transaction = this.db.transaction(['resources', 'images', 'api'], 'readonly');
      
      const resourceCount = await new Promise((resolve) => {
        const store = transaction.objectStore('resources');
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(0);
      });
      
      const imageCount = await new Promise((resolve) => {
        const store = transaction.objectStore('images');
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(0);
      });
      
      const apiCount = await new Promise((resolve) => {
        const store = transaction.objectStore('api');
        const request = store.count();
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => resolve(0);
      });
      
      stats.indexedDB.resources = resourceCount;
      stats.indexedDB.images = imageCount;
      stats.indexedDB.api = apiCount;
      
      return stats;
      
    } catch (error) {
      console.error('获取缓存统计失败:', error);
      return null;
    }
  }

  /**
   * 清空所有缓存
   */
  async clearAllCache() {
    try {
      // 清空内存缓存
      this.memoryCache.clear();
      
      // 清空IndexedDB
      const transaction = this.db.transaction(['resources', 'images', 'api'], 'readwrite');
      await Promise.all([
        transaction.objectStore('resources').clear(),
        transaction.objectStore('images').clear(),
        transaction.objectStore('api').clear()
      ]);
      
      
      console.log('所有缓存已清空');
      
    } catch (error) {
      console.error('清空缓存失败:', error);
    }
  }

  /**
   * 获取备用图片
   * @param {string} url 原图片URL
   * @returns {string} 备用图片
   */
  getFallbackImage(url) {
    if (url.includes('ma.xexexoox10svip1x.com')) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5Yqg6L295LitLi4uPC90ZXh0Pjwvc3ZnPg==';
    }
    
    if (url.includes('admxo.leyou-1.com')) {
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2VlZSIvPjx0ZXh0IHg9IjUwIiB5PSI1MCIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0IiBmaWxsPSIjOTk5IiB0ZXh0LWFuY2hvcj0ibWlkZGxlIiBkeT0iLjNlbSI+5Yqg6L295LitLi4uPC90ZXh0Pjwvc3ZnPg==';
    }
    
    return url;
  }

  /**
   * 将Blob转换为Base64
   * @param {Blob} blob 
   * @returns {Promise<string>}
   */
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  /**
   * 延迟函数
   * @param {number} ms 延迟毫秒数
   * @returns {Promise}
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// 创建全局增强缓存实例
const enhancedCacheManager = new EnhancedCacheManager();

// 定期清理过期缓存（每5分钟）
setInterval(() => {
  enhancedCacheManager.cleanExpiredCache();
}, 5 * 60 * 1000);

export default enhancedCacheManager;
